import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	FormControlLabel,
	Grid,
	InputAdornment,
	List,
	ListItem,
	ListItemText,
	MenuItem,
	Radio,
	Stack,
	TextField,
	Typography,
} from '@mui/material';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import BusinessIcon from '@mui/icons-material/Business';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShieldIcon from '@mui/icons-material/Shield';
import PsychologyIcon from '@mui/icons-material/Psychology';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DescriptionIcon from '@mui/icons-material/Description';
import SaveIcon from '@mui/icons-material/Save';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import MainGrid from '../../components/MainGrid';
import { toast } from 'react-toastify';
import { sanitizeTitle } from '../../Utils/Helpers';
import {
	getAccessToken,
	getElderHomeId,
	getSummaryTemplateEnabled,
	getUserId,
	getUserName,
} from '../../Utils/AuthUtil';
import {
	fetchAllSummarizers,
	fetchSummary,
	generateSummary,
	getResidentIds,
	storeSummary,
} from '../../services/ElderService';
import { useNavigate } from 'react-router-dom';
import { getEnabledUserFeatures } from '../../context/MainProvider';
import { FEATURE_TYPES } from '../../Utils/Enums';
import { useEffect, useState } from 'react';
import { getSummarizerTemplate } from '../../services/CarePlanServices';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { v4 as uuidv4 } from 'uuid';
import { SummaryDocument } from '../../components/SummaryDocument';
import ToastBanner from '../../components/ToastBanner';
import { border, dark, surface, teal } from '../../assets/theme/Colors';

// eslint-disable-next-line no-undef
const central_db_host = process.env.REACT_APP_UMS_API_URL;

const INITIAL_ERROR_STATE = { status: false, msg: '' };

const SUMMARY_TEMPLATES = {
	narrative: 'Narrative Summary',
	structured: 'Structured Summary',
	instruct: 'Custom Prompt Summary',
};

const TABS = {
	rs: 'Resident Summarizer',
	cs: 'Client Summarizer',
};

const promptSuggestions = [
	{
		label: 'Falls this month',
		value: 'Summarize all residents with fall events this month and their current care plan status',
	},
	{
		label: 'Dementia wellbeing',
		value: 'Give me a wellbeing summary for all dementia residents showing engagement trends',
	},
	{
		label: 'Overdue plans',
		value: 'List all residents whose care plans are overdue for review with key risk flags',
	},
	{
		label: 'Incident review',
		value: 'Summarize all incidents and near-misses in the last 30 days across all residents',
	},
];

const templates = [
	{
		title: 'Narrative Summary',
		uiLabel: 'Clinic Handover Summary',
		desc: '',
		icon: <BusinessIcon fontSize="small" />,
	},
	{
		title: 'Structured Summary',
		uiLabel: 'Resident Care Profile',
		desc: '',
		icon: <FavoriteIcon fontSize="small" />,
	},
	{
		title: 'Custom Prompt Summary',
		uiLabel: 'Custom Prompt Summary',
		desc: '',
		icon: <PsychologyIcon fontSize="small" />,
	},
];

export default function DetailSummarizer() {
	const navigate = useNavigate();
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const isTemplateEnabled = getSummaryTemplateEnabled();
	const { enabledUserFeatureList } = getEnabledUserFeatures();
	const isThisFeatureDisabled = !enabledUserFeatureList.includes(
		FEATURE_TYPES[4].key,
	);
	const [id, setId] = useState(null);
	const [viewType, setViewType] = useState(null);
	const [residentIds, setResidentIds] = useState([]);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [tabValue, setTabValue] = useState(1);
	const [text, setText] = useState('');
	const [fileData, setFileData] = useState([]);
	const [txtError, setTxtError] = useState(INITIAL_ERROR_STATE);
	const [fileError, setFileError] = useState(INITIAL_ERROR_STATE);
	const [summaryData, setSummaryData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [isSummaryGenerated, setIsSummaryGenerated] = useState(false);
	const [summarizedData, setSummarizedData] = useState([]);
	const [isEditing, setIsEditing] = useState(false);
	const [singleSummary, setSingleSummary] = useState({});
	const [isStoringData, setIsStoringData] = useState(false);
	const [isSummaryEmpty, setIsSummaryEmpty] = useState(false);
	const [scroll, setScroll] = useState(false);
	const [showSummaryTemplates, setShowSummaryTemplates] = useState(false);
	const [selectedTemplate, setSelectedTemplate] = useState('');
	const [showNoResidentBanner, setShowNoResidentBanner] = useState(false);
	const [instruction, setInstruction] = useState('');
	// const [templates, setTemplates] = useState([]);
	const [residentSummaryTemplates, setResidentSummaryTemplates] = useState(
		[],
	);
	const [clientSummaryTemplates, setClientSummaryTemplates] = useState([]);
	const [summaryTypeTab, setSummaryTypeTab] = useState(TABS.rs);
	const [onShowClientSummaryInputs, setOnShowClientSummaryInputs] =
		useState(false);
	const [summaryEndpoints, setSummaryEndpoints] = useState([]);
	const formattedDate = new Date().toLocaleDateString('en-US', {
		month: 'long',
		year: 'numeric',
	});

	useEffect(() => {
		fetchResidentIds();
		fetchSummarizersData();
		fetchSummarizerTemplateFiles();
	}, []);

	async function fetchResidentIds() {
		const response = await getResidentIds(homeId, token);

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);
			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
			setShowNoResidentBanner(response.content.length > 0 ? false : true);
		} else {
			toast.error(response.message);
		}
	}

	async function fetchSummarizersData() {
		const response = await fetchAllSummarizers(homeId, token);

		if (!response.error) {
			setSummaryData(response);
		} else {
			toast.error(response.message);
		}
	}

	async function fetchSummarizerTemplateFiles() {
		const cResponse = await getSummarizerTemplate(homeId, 'client');
		if (!cResponse.error) {
			const temp = cResponse.templates.filter(
				item =>
					![
						'narrative_summary',
						'structured_summary',
						'instruction_based_summary',
					].includes(item.template_key),
			);
			setClientSummaryTemplates(temp);
			// isInitialFetch && setTemplates(temp);
		}

		const eResponse = await getSummarizerTemplate(homeId, 'elder');
		if (!eResponse.error) {
			const temp = eResponse.templates.filter(
				item =>
					![
						'narrative_summary',
						'structured_summary',
						'instruction_based_summary',
					].includes(item.template_key),
			);
			setResidentSummaryTemplates(temp);
		}
	}

	function readTextFile(e) {
		if (fileData.length === 5) {
			setFileError({
				status: true,
				msg: 'Maximum files: 5 files per upload.',
			});

			return;
		}

		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx'].includes(ext)) {
			setFileError({
				status: true,
				msg: 'Invalid file format',
			});
			return;
		}

		if (e[0] && e[0].size === 0) {
			setFileError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		setFileError(INITIAL_ERROR_STATE);
		setFileData([...fileData, e[0]]);
	}

	function onRemoveFiles(content) {
		const clone = fileData.filter(value => value.name !== content.name);
		setFileData(clone);
	}

	function validateInputs() {
		let isValid = true;

		if (tabValue === 0) {
			if (!text || text === '') {
				setTxtError({ status: true, msg: 'Enter medical information' });
				isValid = false;
			}
		} else {
			if (fileData.length === 0) {
				setFileError({ status: true, msg: 'Upload the files' });
				isValid = false;
			}
		}

		return isValid;
	}

	function getTemplateKey(value) {
		if (value === SUMMARY_TEMPLATES.narrative) {
			return 'narrative_summary';
		} else if (value === SUMMARY_TEMPLATES.instruct) {
			return 'instruction_based_summary';
		} else if (value === SUMMARY_TEMPLATES.structured) {
			return 'structured_summary';
		} else {
			return value;
		}
	}

	async function onGenerateSummary() {
		setSummarizedData([]);
		setIsLoading(true);
		setIsSummaryGenerated(false);
		const isClientSummary = summaryTypeTab === TABS.cs;

		const formData = new FormData();
		formData.append('client_id', getElderHomeId());
		formData.append('base_url', central_db_host);
		formData.append('auth_token', token);
		formData.append('summary_format', 'html');

		formData.append(
			isClientSummary ? 'template_key' : 'template_type',
			getTemplateKey(selectedTemplate),
		);
		if (
			selectedTemplate === SUMMARY_TEMPLATES.instruct &&
			instruction &&
			instruction !== ''
		) {
			formData.append('instructions', instruction);
		}

		if (id && id !== '' && !isClientSummary) {
			const selectedElder = residentFetchedIds.find(
				item => item.elderId === id.split(' ')[0] || item.name === id,
			);

			formData.append(
				'elder_id',
				selectedElder.isFromExternalApi
					? selectedElder.elderId
					: selectedElder.id,
			);
			formData.append('use_api', selectedElder.isFromExternalApi);
		}

		if (tabValue === 0 && text !== '') {
			formData.append('input_text', text);
		} else if (tabValue === 1 && fileData.length !== 0) {
			fileData.forEach(file => {
				formData.append(
					isClientSummary ? 'template_file' : 'uploaded_files',
					new Blob([file], { type: 'text/plain' }),
					file.name,
				);
			});
		}

		if (
			isClientSummary &&
			summaryEndpoints &&
			summaryEndpoints.length > 0
		) {
			summaryEndpoints.forEach(url => {
				formData.append('client_summary_endpoints', url);
			});
		}

		const route = isClientSummary ? 'summarise_client' : 'summarise_elder';
		const responseData = await generateSummary(formData, route);

		if (!responseData.error) {
			if (isClientSummary) {
				const summary = responseData.summary_text;
				if (summary.length !== 0) {
					setIsSummaryEmpty(false);
					setSummarizedData(summary);
				} else {
					setIsSummaryEmpty(true);
					toast.error('No summary generated');
				}
			} else {
				if (selectedTemplate === SUMMARY_TEMPLATES.narrative) {
					const summary = responseData.summarised_information.summary;

					const isEmpty =
						!summary ||
						(Array.isArray(summary) && summary.length === 0) ||
						(typeof summary === 'object' &&
							!Array.isArray(summary) &&
							Object.keys(summary).length === 0);

					if (!isEmpty) {
						setIsSummaryEmpty(false);
						setSummarizedData(summary);
					} else {
						setIsSummaryEmpty(true);
						toast.error('No summary generated');
					}
				} else {
					const summary = responseData.summarised_information;
					if (Object.keys(summary).length !== 0) {
						const formattedSummary = Object.keys(summary)
							.filter(
								value =>
									![
										'Elder_ID',
										'Generated_On',
										'Template',
									].includes(value) && value.length > 0,
							)
							.map(item => {
								const bodyContent = checkValueTypes(
									summary[item],
								);
								if (bodyContent.length > 0) {
									return {
										title: sanitizeTitle(item),
										bodyContent: bodyContent,
									};
								}

								return null;
							})
							.filter(item => item !== null);

						setIsSummaryEmpty(false);

						setSummarizedData(formattedSummary);
					} else {
						setIsSummaryEmpty(true);
						toast.error('No summary generated');
					}
				}
			}
			setIsSummaryGenerated(true);
			setShowSummaryTemplates(false);
		} else {
			toast.error(responseData.message);
		}

		setScroll(true);
		setIsLoading(false);
		setTimeout(() => {
			setScroll(false);
		}, 2000);
	}

	function checkValueTypes(item) {
		if (
			Array.isArray(item) &&
			item.every(value => typeof value === 'object')
		) {
			const mergedArray = item.flatMap(obj =>
				Object.entries(obj).map(([key, value]) => `${key}: ${value} `),
			);

			return mergedArray;
		} else if (
			item !== null &&
			typeof item === 'object' &&
			!Array.isArray(item)
		) {
			const mergedArray = [];

			for (const [key, value] of Object.entries(item)) {
				if (
					typeof value === 'object' &&
					value !== null &&
					!Array.isArray(value)
				) {
					const nested = checkValueTypes(value);
					nested.forEach(entry => {
						mergedArray.push(`${entry} `);
					});
				} else {
					mergedArray.push(`${key}: ${value} `);
				}
			}

			return mergedArray;
		} else if (typeof item === 'string') {
			return [item];
		} else {
			return item;
		}
	}

	function onEnableEdit() {
		setIsEditing(!isEditing);
	}

	async function onGeneratePdf() {
		const fileName = `SenzCare.AI_Summary_${id ? id : uuidv4()}.pdf`;
		const blob = await pdf(
			<SummaryDocument
				documentData={
					summarizedData.length > 0
						? summarizedData
						: singleSummary.summaries
				}
				title={'Generated Medical Summary'}
			/>,
		).toBlob();
		saveAs(blob, fileName);
	}

	async function onSaveSummaries() {
		setIsStoringData(true);
		let selectedElderId = '';
		let elderData = '';
		if (id && id !== '') {
			const selectedElder = residentFetchedIds.find(
				item => item.elderId === id.split(' ')[0] || item.name === id,
			);

			selectedElderId = selectedElder.id;
			elderData = `${selectedElder.elderId}: ${selectedElder.name} `;
		}

		const summaryType =
			fileData && fileData.length > 0 && tabValue === 1
				? 'file'
				: text && text !== '' && tabValue === 0
					? 'text'
					: 'none';

		const isClientSummary = summaryTypeTab === TABS.cs;
		const summaryNewData = {
			elderHomeId: homeId,
			elderId: selectedElderId,
			elder: elderData,
			generatedUserId: getUserId(),
			generatedUserName: getUserName(),
			summaryText: text,
			summaryType: summaryType,
			summaries:
				selectedTemplate === SUMMARY_TEMPLATES.narrative ||
				isClientSummary
					? [{ paragraph: summarizedData }]
					: summarizedData,
			summaryTemplate: selectedTemplate,
			summaryVersion: isClientSummary ? 'client' : 'resident',
		};

		const formData = new FormData();
		formData.append(
			'summary',
			new Blob([JSON.stringify(summaryNewData)], {
				type: 'application/json',
			}),
		);

		if (tabValue === 1) {
			fileData.forEach(file => {
				formData.append('file', file);
			});
		}

		const response = await storeSummary(formData, homeId, token);

		if (!response.error) {
			toast.success('Summary stored successfully');
			onCloseAll();
			fetchSummarizersData();
		} else {
			toast.error(response.message);
		}
		setIsStoringData(false);
	}

	function onCloseAll() {
		setId(null);
		setIsSummaryGenerated(false);
		setText('');
		setFileData([]);
		setSummarizedData([]);
		setSelectedTemplate('');
		setInstruction('');
		setSummaryTypeTab(TABS.rs);
		setOnShowClientSummaryInputs(false);
		setFileError({ status: false, msg: '' });
	}

	function convertBulletStringToArray(str) {
		return str.split('\n').map(line => line.replace(/^•\s*/, ''));
	}

	function handleSummaryChange(value, mainIndex) {
		const updatedItems = convertBulletStringToArray(value);
		const clone = summarizedData.map((item, index) => {
			if (mainIndex === index) {
				return {
					...item,
					bodyContent: updatedItems,
				};
			} else {
				return item;
			}
		});

		setSummarizedData(clone);
	}

	async function onFetchSummary(id) {
		const token = getAccessToken();
		const response = await fetchSummary(id, token);

		if (response.message === 'Success') {
			const data = response.content;

			if (data.summaries[0].paragraph) {
				setSelectedTemplate(SUMMARY_TEMPLATES.narrative);
				const updatedData = {
					...data,
					summaries: data.summaries[0].paragraph,
				};

				setSingleSummary(updatedData);
			} else {
				setSingleSummary(data);
				setSelectedTemplate(data.summaryTemplate);
			}
			setViewType('FullSummary');
			setId(data.elder);
		} else {
			toast.error(response.message);
		}
	}

	function onCheckValueSelected() {
		if (!id && summaryTypeTab !== TABS.cs) {
			if (tabValue === 0 && text === '') {
				return true;
			}

			if (tabValue === 1 && fileData.length == 0) {
				return true;
			}
		}

		return false;
	}

	async function onSelect(item) {
		// setTemplates(
		// 	item === TABS.cs
		// 		? clientSummaryTemplates
		// 		: residentSummaryTemplates,
		// );
		setFileData([]);
		setText('');
		setTxtError({ status: false, msg: '' });
		setSummaryTypeTab(item);
		setOnShowClientSummaryInputs(false);
		setSelectedTemplate('');
		setFileError({ status: false, msg: '' });
		setId(null);
	}

	return (
		<MainGrid>
			<Box
				sx={{
					width: '100%',
				}}>
				{/* Header */}
				<Box sx={{ mb: 3 }}>
					<Typography
						sx={{
							fontSize: '22px',
							fontWeight: 600,
							color: dark,
							mb: 0.5,
						}}>
						Detail Summarizer
					</Typography>

					<Typography
						sx={{
							fontSize: 14,
							color: '#6b8888',
						}}>
						Generate resident and organization summaries — saved to
						profiles automatically
					</Typography>
				</Box>

				<Grid container spacing={2}>
					{/* Left */}
					<Grid item xs={12} md={5}>
						<Card
							sx={{
								borderRadius: '18px',
								border: '1px solid #d8e8e8',
								boxShadow: 'none',
							}}>
							<CardContent
								sx={{
									p: 3,
									minHeight: 0,
									height: '100%',
									display: 'flex',
									flexDirection: 'column',
								}}>
								<Box
									sx={{
										flex: 1,
										minHeight: 0,
										overflowY: 'auto',
										pr: 1,
										scrollbarWidth: 'none', // Firefox
										'&::-webkit-scrollbar': {
											display: 'none', // Chrome, Safari, Edge
										},
									}}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: 1,
											mb: 3,
											overflow: 'auto',
										}}>
										<SettingsSuggestIcon
											sx={{
												fontSize: 18,
												color: teal,
											}}
										/>
										<Typography
											sx={{
												fontSize: 13,
												fontWeight: 500,
												color: dark,
											}}>
											Configure Summary
										</Typography>
									</Box>

									{/* Summary Type */}
									<Box sx={{ mb: 3 }}>
										<Typography
											sx={{
												fontSize: 12,
												fontWeight: 600,
												color: '#6b8888',
												mb: 1,
											}}>
											Summary Type
										</Typography>
										<Stack paddingLeft={1}>
											{/* <FormControlLabel
												value={0}
												disabled={isLoading}
												control={
													<Radio
														checked={tabValue === 0}
														onChange={() => {
															setTabValue(0);
															setSummaryTypeTab(
																TABS.cs,
															);
															setSummarizedData(
																[],
															);
															setSelectedTemplate(
																'',
															);
														}}
														clickable
														size="small"
														sx={{
															color: teal,
															'&.Mui-checked': {
																color: teal,
															},
															cursor: 'pointer',
														}}
													/>
												}
												sx={{
													'& .MuiFormControlLabel-label':
														{
															fontSize: '13px',
														},
												}}
												label="Organization-wide summary"
											/> */}
											<FormControlLabel
												value={1}
												disabled={isLoading}
												control={
													<Radio
														checked={tabValue === 1}
														onChange={() => {
															setTabValue(1);
															setSummaryTypeTab(
																TABS.rs,
															);
															setSummarizedData(
																[],
															);
															setSelectedTemplate(
																'',
															);
														}}
														size="small"
														sx={{
															color: teal,
															'&.Mui-checked': {
																color: teal,
															},
															cursor: 'pointer',
														}}
													/>
												}
												sx={{
													'& .MuiFormControlLabel-label':
														{
															fontSize: '13px',
														},
												}}
												label="Resident summary (individual)"
											/>
										</Stack>
									</Box>

									{tabValue === 1 && (
										<Box sx={{ mb: 3 }}>
											<Typography
												sx={{
													fontSize: 12,
													fontWeight: 600,
													color: '#6b8888',
													mb: 1,
												}}>
												Residents
											</Typography>
											<TextField
												select
												fullWidth
												disabled={isLoading}
												size="small"
												onChange={e => {
													setId(e.target.value);
													setSummarizedData([]);
													setSelectedTemplate('');
												}}
												defaultValue="Select Resident"
												sx={{
													'& .MuiSelect-select': {
														fontSize: '12px',
													},
												}}>
												<MenuItem
													value="Select Resident"
													disabled
													sx={{ fontSize: '12px' }}>
													Select Resident
												</MenuItem>
												{residentIds.map(
													(item, index) => (
														<MenuItem
															key={index}
															value={item}
															sx={{
																fontSize:
																	'12px',
															}}>
															{item}
														</MenuItem>
													),
												)}
											</TextField>
										</Box>
									)}

									{/* Templates */}
									<Box sx={{ mb: 3 }}>
										<Typography
											sx={{
												fontSize: 12,
												fontWeight: 600,
												color: '#6b8888',
												mb: 1,
											}}>
											Template
										</Typography>

										<Stack spacing={1}>
											{templates.map(item => (
												<Box
													key={item.title}
													onClick={() =>
														setSelectedTemplate(
															item.title,
														)
													}
													sx={{
														pointerEvents: isLoading
															? 'none'
															: 'auto',
														opacity: isLoading
															? 0.5
															: 1,
														border:
															selectedTemplate ===
															item.title
																? '1px solid #b3ede8'
																: '1px solid #d8e8e8',
														background:
															selectedTemplate ===
															item.title
																? '#eefcf9'
																: '#fff',
														borderRadius: '12px',
														p: 1.5,
														display: 'flex',
														alignItems: 'center',
														gap: 1.5,
														cursor: 'pointer',
														transition: '0.2s',
														'&:hover': {
															borderColor: teal,
															background:
																'#eefcf9',
														},
													}}>
													<Box
														sx={{
															width: 36,
															height: 36,
															borderRadius:
																'10px',
															background:
																selectedTemplate ===
																item.title
																	? 'rgba(0,196,180,0.15)'
																	: surface,
															display: 'flex',
															alignItems:
																'center',
															justifyContent:
																'center',
															color: item.active
																? teal
																: '#6b8888',
														}}>
														{item.icon}
													</Box>

													<Box sx={{ flex: 1 }}>
														<Typography
															sx={{
																fontSize: 13,
																fontWeight: 600,
																color: '#1a2424',
															}}>
															{item.uiLabel}
														</Typography>

														<Typography
															sx={{
																fontSize: 11,
																color: '#6b8888',
															}}>
															{item.desc}
														</Typography>
													</Box>

													{selectedTemplate ===
														item.title && (
														<CheckCircleIcon
															sx={{
																fontSize: 18,
																color: teal,
															}}
														/>
													)}
												</Box>
											))}
										</Stack>
									</Box>

									{selectedTemplate ===
										'Custom Prompt Summary' && (
										<Box sx={{ mb: 2 }}>
											{/* Label */}
											<Typography
												sx={{
													fontSize: '13px',
													fontWeight: 600,
													color: '#0f172a',
													mb: 1,
												}}>
												Describe the summary you need
											</Typography>

											{/* Prompt Input */}
											<TextField
												fullWidth
												multiline
												minRows={3}
												value={instruction}
												onChange={e =>
													setInstruction(
														e.target.value,
													)
												}
												placeholder="e.g. Give me a brief summary of all residents with dementia who had any incidents in the last 2 weeks, including what happened and what actions were taken…"
												sx={{
													mb: 1.2,
													'& .MuiOutlinedInput-root':
														{
															borderRadius: '5px',
															backgroundColor:
																'#fff',
															alignItems:
																'flex-start',
															pr: 1,
														},

													'& textarea': {
														fontSize: '14px',
														lineHeight: 1.6,
														color: '#334155',
													},
												}}
											/>

											{/* Suggestion Pills */}
											{/* <Stack
												direction="row"
												spacing={1}
												useFlexGap
												flexWrap="wrap">
												{promptSuggestions.map(item => (
													<Chip
														key={item.label}
														label={item.label}
														onClick={() =>
															setInstruction(
																item.value,
															)
														}
														sx={{
															height: 32,
															borderRadius:
																'999px',
															backgroundColor:
																'#f8fafc',
															border: '1px solid #e2e8f0',
															fontSize: '13px',
															fontWeight: 500,
															color: '#334155',
															cursor: 'pointer',

															'&:hover': {
																backgroundColor:
																	'#f1f5f9',
															},
														}}
													/>
												))}
											</Stack> */}
										</Box>
									)}

									<Button
										fullWidth
										onClick={onGenerateSummary}
										startIcon={<AutoAwesomeIcon />}
										disabled={isLoading}
										sx={{
											height: 44,
											borderRadius: '10px',
											background: teal,
											color: dark,
											fontSize: 13,
											fontWeight: 700,
											textTransform: 'none',
											boxShadow: 'none',
											'&:hover': {
												background: '#00b0a0',
												boxShadow: 'none',
											},
										}}>
										Generate Summary
									</Button>
								</Box>
							</CardContent>
						</Card>
					</Grid>

					{/* Right */}
					<Grid item xs={12} md={7}>
						{summarizedData.length > 0 ? (
							<Card
								sx={{
									borderRadius: '18px',
									border: '1px solid #d8e8e8',
									boxShadow: 'none',
									height: '100vh',
								}}>
								<CardContent
									sx={{
										p: 3,
										display: 'flex',
										flexDirection: 'column',
										height: '100%',
									}}>
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'space-between',
											mb: 2,
										}}>
										<Box
											sx={{
												display: 'flex',
												alignItems: 'center',
												gap: 1,
											}}>
											<DescriptionIcon
												sx={{
													fontSize: 18,
													color: teal,
												}}
											/>

											<Typography
												sx={{
													fontSize: 14,
													fontWeight: 700,
													color: dark,
												}}>
												{`Generated - ${selectedTemplate}
												Overview`}
											</Typography>
										</Box>

										<Typography
											sx={{
												fontSize: 11,
												color: '#6b8888',
											}}>
											{formattedDate}
										</Typography>
									</Box>

									{/* Document Preview */}
									<Box
										sx={{
											flex: 1,
											overflowY: 'auto',
											minHeight: 0, // IMPORTANT for flex scrolling
											pr: 1,
										}}>
										{selectedTemplate ===
										SUMMARY_TEMPLATES.narrative ? (
											<Typography
												sx={{
													fontSize: 12,
													color: '#6b8888',
													lineHeight: 1.9,
													whiteSpace: 'pre-line',
												}}
												dangerouslySetInnerHTML={{
													__html: summarizedData,
												}}
											/>
										) : (
											Array.isArray(summarizedData) &&
											summarizedData.length > 0 && (
												<>
													{summarizedData.map(
														(section, index) => (
															<Box
																key={index}
																sx={{ mb: 2 }}>
																{section.title && (
																	<Typography
																		sx={{
																			fontSize: 13,
																			fontWeight: 700,
																			mb: '5px',
																			color: '#6b8888',
																		}}>
																		{
																			section.title
																		}
																	</Typography>
																)}

																{section.bodyContent && (
																	<List
																		dense
																		sx={{
																			pl: 1,
																		}}>
																		{section.bodyContent.map(
																			(
																				item,
																				i,
																			) => (
																				<ListItem
																					key={
																						i
																					}
																					sx={{
																						py: 0,
																					}}>
																					<ListItemText
																						primary={`• ${item}`}
																						primaryTypographyProps={{
																							fontSize: 12,
																							color: '#6b8888',
																							lineHeight: 1.9,
																						}}
																					/>
																				</ListItem>
																			),
																		)}
																	</List>
																)}
															</Box>
														),
													)}
												</>
											)
										)}
									</Box>

									{/* Actions */}
									<Stack
										direction="row"
										spacing={1}
										sx={{ mt: 2 }}>
										<Button
											onClick={onSaveSummaries}
											startIcon={<SaveIcon />}
											sx={{
												background: teal,
												color: dark,
												textTransform: 'none',
												fontSize: 13,
												fontWeight: 700,
												borderRadius: '10px',
												px: 2,
												'&:hover': {
													background: '#00b0a0',
												},
											}}>
											Save to Profile
										</Button>

										<Button
											onClick={onGeneratePdf}
											variant="outlined"
											startIcon={<DownloadIcon />}
											sx={{
												borderColor: border,
												color: '#1a2424',
												textTransform: 'none',
												fontSize: 13,
												borderRadius: '10px',
												'&:hover': {
													borderColor: teal,
													background: surface,
												},
											}}>
											Download
										</Button>

										{/* <Button
											variant="outlined"
											startIcon={<EditIcon />}
											sx={{
												borderColor: border,
												color: '#1a2424',
												textTransform: 'none',
												fontSize: 13,
												borderRadius: '10px',
												'&:hover': {
													borderColor: teal,
													background: surface,
												},
											}}>
											Edit
										</Button> */}
									</Stack>
								</CardContent>
							</Card>
						) : (
							<Card
								sx={{
									borderRadius: '18px',
									border: '1px solid #d8e8e8',
									boxShadow: 'none',
									alignItems: 'center',
								}}>
								<CardContent
									sx={{
										p: 3,
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										minHeight: 300,
										color: '#6b8888',
										fontSize: 13,
										fontWeight: 500,
									}}>
									No summary available. Generate a care
									summary overview to see results here.
								</CardContent>
							</Card>
						)}
					</Grid>
				</Grid>
			</Box>
			<ToastBanner
				open={isLoading}
				message={'Generating Care Summary...'}
				icon={<AutoAwesomeIcon color={'#FFFFFF'} />}
				isCompleted={isSummaryGenerated}
			/>
		</MainGrid>
	);
}
